import { readAllBytes, type Bridge } from '@casa/bridge';
import { parseDashboard, SCHEMA_VERSION, writeDashboard, type Dashboard, type TileOrigin } from '@casa/model';
import { listServerDashboards, readServerDashboard, serverLink, writeServerDashboard, type ServerStore, type WriteCondition } from './casa_server.ts';
import { findDeviceDashboard, freeDeviceName, readDeviceDashboard, writeDeviceDashboard } from './device_store.ts';
import { dashboardBySlug, dashboardSlug, tabDashboard, tabStoragePrefix } from './tab_dashboard.ts';

// The dashboard this device shows: a cached copy in the app's storage, and where it comes from, a
// dashboard in one of the stores (docs/specs/settings.md): this device, the Tilez Hub, or a web
// address. The app starts from the copy; a reload reads the store again and replaces the copy only
// with a file that validates, so a store that fails or serves a broken file leaves the dashboard as
// it was. The copy and the source's settings hold credentials (decision §12): neither is logged.

/** The cached copy, relative to the storage root (where C2 kept the dashboard). */
export const DASHBOARD_PATH = 'dashboard.json';
/** Where the dashboard comes from. */
export const SOURCE_PATH = 'dashboard-source.json';

// A browser tab at /dash/<name> keeps its own copy and source, under a prefix (tab_dashboard.ts).
const cachePath = () : string => `${tabStoragePrefix()}${DASHBOARD_PATH}`;
const sourcePath = () : string => `${tabStoragePrefix()}${SOURCE_PATH}`;

const FETCH_TIMEOUT_MS = 15_000;

/**
 * A dashboard kept on this device, by name; one the Tilez Hub keeps, by name, with the version
 * this device has, which Save writes back (none yet: made here, not saved there); or the one at a
 * web address, fetched with an `Authorization` header if the server wants one, which is read-only.
 * Cloud stores, later, add kinds here.
 */
export type DashboardSource =
    | { kind: 'device'; name: string }
    | { kind: 'casa'; name: string; version?: string | undefined }
    | { kind: 'cloud'; name: string; version?: string | undefined }
    | { kind: 'url'; url: string; authorization?: string };

/** Where a server store's dashboards are, for the user. */
export const SERVER_NAMES: Readonly<Record<ServerStore, string>> = { casa: 'the Tilez Hub', cloud: 'Tilez Cloud' };

export class DashboardError extends Error {
    override name = 'DashboardError';
    readonly issues: readonly string[];

    constructor(message: string, issues: readonly string[] = [], options?: ErrorOptions) {
        super(message, options);
        this.issues = issues;
    }
}

export function parseOrThrow(text: string, where: string) : Dashboard {
    const result = parseDashboard(text);
    if (!result.ok) {
        throw new DashboardError(`The dashboard ${where} is not valid.`, result.issues);
    }

    return result.dashboard;
}

/** Why a web address cannot be a source, or undefined when it can. */
export function checkSourceUrl(url: string) : string | undefined {
    let parsed: URL;
    try {
        parsed = new URL(url.trim());
    } catch {
        return 'This is not a web address.';
    }

    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? undefined : 'The address must start with http:// or https://.';
}

/** A web address whose traffic anyone on the network path can read: the file holds passwords and tokens. */
export function isUnencryptedUrl(url: string) : boolean {
    return url.trim().toLowerCase().startsWith('http:');
}

/** Where the dashboard comes from, for the user; never the authorization. */
export function describeSource(source: DashboardSource) : string {
    switch (source.kind) {
        case 'device':
            return `"${source.name}" on this device`;
        case 'casa':
        case 'cloud':
            return `"${source.name}" on ${SERVER_NAMES[source.kind]}`;
        case 'url':
            return source.url;
    }
}

/** The cached copy; undefined when there is none. Throws `DashboardError` when it does not validate. */
export async function readCachedDashboard(bridge: Bridge) : Promise<Dashboard | undefined> {
    const stored = await bridge.storage.read(cachePath());

    return stored ? parseOrThrow(new TextDecoder().decode(stored), 'in storage') : undefined;
}

/** What `SOURCE_PATH` held before the stores (Phase 3 E3b): a file read once, or a dashboard made here. */
type LegacySource = { kind: 'file'; name: string } | { kind: 'local' };

function parseSource(value: unknown) : DashboardSource | LegacySource | undefined {
    if (typeof value !== 'object' || value === null) {
        return undefined;
    }

    const candidate = value as Record<string, unknown>;
    switch (candidate.kind) {
        case 'device':
        case 'file':
            return typeof candidate.name === 'string' ? (candidate as DashboardSource | LegacySource) : undefined;
        case 'casa':
        case 'cloud':
            return typeof candidate.name === 'string' && (candidate.version === undefined || typeof candidate.version === 'string') ? (candidate as DashboardSource) : undefined;
        case 'url':
            return typeof candidate.url === 'string' ? (candidate as DashboardSource) : undefined;
        case 'local':
            return { kind: 'local' };
        default:
            return undefined;
    }
}

async function readStoredSource(bridge: Bridge) : Promise<DashboardSource | LegacySource | undefined> {
    const stored = await bridge.storage.read(sourcePath());
    if (!stored) {
        return undefined;
    }

    try {
        return parseSource(JSON.parse(new TextDecoder().decode(stored)));
    } catch {
        return undefined;
    }
}

async function writeSource(bridge: Bridge, source: DashboardSource) : Promise<void> {
    await bridge.storage.write(sourcePath(), new TextEncoder().encode(`${JSON.stringify(source, null, 2)}\n`));
}

/** Where the shown dashboard comes from; undefined when there is no dashboard yet. */
export async function readSource(bridge: Bridge) : Promise<DashboardSource | undefined> {
    const source = await readStoredSource(bridge);

    return source?.kind === 'file' || source?.kind === 'local' ? undefined : source;
}

/** A name for a dashboard from a file or an address: its last part without `.json`, or `fallback`. */
export function suggestedName(fromName: string | undefined, fallback = 'Dashboard') : string {
    const base = (fromName ?? '')
        .split('/')
        .filter((part) => part !== '')
        .at(-1)
        ?.replace(/\.json$/i, '')
        .replace(/[^A-Za-z0-9 ._-]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    return base && /^[A-Za-z0-9]/.test(base) ? base.slice(0, 64).trim() : fallback;
}

/**
 * Before the stores, a device kept one dashboard: from a file, made here, or with no source at all
 * (set up before Phase 1 C5b). That copy becomes a dashboard on this device, under a name, so that
 * nothing a user had is lost. Does nothing when there is nothing to move.
 */
export async function moveLegacyDashboard(bridge: Bridge) : Promise<void> {
    const source = await readStoredSource(bridge);
    if (source && source.kind !== 'file' && source.kind !== 'local') {
        return;
    }

    const stored = await bridge.storage.read(cachePath());
    if (!stored) {
        return;
    }

    const name = await freeDeviceName(bridge, source?.kind === 'file' ? suggestedName(source.name, 'My dashboard') : 'My dashboard');
    await writeDeviceDashboard(bridge, name, new TextDecoder().decode(stored));
    await writeSource(bridge, { kind: 'device', name });
}

/** The dashboard's text from a web address, through the bridge. */
export async function fetchFromUrl(bridge: Bridge, address: { url: string; authorization?: string | undefined }) : Promise<string> {
    const problem = checkSourceUrl(address.url);
    if (problem) {
        throw new DashboardError(problem);
    }

    const response = await bridge.net.http.request({
        method: 'GET',
        // As the browser parsed it: `http:/host`, which the browser accepts, becomes `http://host/`
        // for every bridge host.
        url: new URL(address.url.trim()).href,
        headers: address.authorization ? { authorization: address.authorization } : {},
        timeoutMs: FETCH_TIMEOUT_MS,
    });
    const body = await readAllBytes(response.body);
    if (response.status !== 200) {
        throw new DashboardError(`${address.url} answered ${response.status}.`);
    }

    return new TextDecoder().decode(body);
}

/**
 * Takes a dashboard file as the one this device shows: validates it, then stores it as the cached
 * copy and `source` as where it came from. Nothing is stored when it does not validate.
 */
export async function adoptDashboard(bridge: Bridge, text: string, source: DashboardSource) : Promise<Dashboard> {
    const dashboard = parseOrThrow(text, `from ${describeSource(source)}`);
    // Stored as it came, so that the stored file is the one that was validated.
    await bridge.storage.write(cachePath(), new TextEncoder().encode(text));
    await writeSource(bridge, source);

    return dashboard;
}

/** Fetches the dashboard from a web address and shows it. */
export async function loadFromUrl(bridge: Bridge, source: Extract<DashboardSource, { kind: 'url' }>) : Promise<Dashboard> {
    return adoptDashboard(bridge, await fetchFromUrl(bridge, source), source);
}

/** The link to a server store; throws when this device is not paired with it. */
async function linkOf(bridge: Bridge, store: ServerStore) : Promise<NonNullable<Awaited<ReturnType<typeof serverLink>>>> {
    const link = await serverLink(bridge, store);
    if (!link) {
        throw new DashboardError(`This device is not paired with ${SERVER_NAMES[store]}: pair it in the settings.`);
    }

    return link;
}

/** Loads a dashboard the Tilez Hub, or Tilez Cloud, keeps, by name, and shows it with its version. */
export async function loadFromServer(bridge: Bridge, name: string, store: ServerStore = 'casa') : Promise<Dashboard> {
    const { text, version } = await readServerDashboard(bridge, await linkOf(bridge, store), name);

    return adoptDashboard(bridge, text, { kind: store, name, version });
}

/** Loads a dashboard kept on this device and shows it. */
export async function loadFromDevice(bridge: Bridge, name: string) : Promise<Dashboard> {
    const text = await readDeviceDashboard(bridge, name);
    if (text === undefined) {
        throw new DashboardError(`There is no dashboard called "${name}" on this device.`);
    }

    return adoptDashboard(bridge, text, { kind: 'device', name });
}

/** Reads the dashboard again from where it comes from, and shows it. */
export async function loadSource(bridge: Bridge, source: DashboardSource) : Promise<Dashboard> {
    switch (source.kind) {
        case 'device':
            return loadFromDevice(bridge, source.name);
        case 'casa':
        case 'cloud':
            if (source.version === undefined) {
                throw new DashboardError(`"${source.name}" has not been saved on ${SERVER_NAMES[source.kind]} yet.`);
            }

            return loadFromServer(bridge, source.name, source.kind);
        case 'url':
            return loadFromUrl(bridge, source);
    }
}

/**
 * The dashboard the app starts with: the cached copy; without one, it is loaded from its source
 * once (a copy can be lost while its source is kept). Undefined when there is neither, which is a
 * first start. Throws when the copy does not validate or the source fails.
 */
export async function startingDashboard(bridge: Bridge) : Promise<Dashboard | undefined> {
    const tab = tabDashboard();
    if (tab !== undefined) {
        return startingTabDashboard(bridge, tab);
    }

    await moveLegacyDashboard(bridge);
    const cached = await readCachedDashboard(bridge);
    if (cached) {
        return cached;
    }

    const source = await readSource(bridge);

    return source ? loadSource(bridge, source) : undefined;
}

/**
 * A tab's dashboard, named in its path (/dash/<name>): its own copy when that is the dashboard the
 * path names, else the server's, by the exact name or its slug. Throws when the server has none.
 */
async function startingTabDashboard(bridge: Bridge, slug: string) : Promise<Dashboard> {
    const source = await readSource(bridge);
    if (source?.kind === 'casa' && dashboardSlug(source.name) === slug) {
        const cached = await readCachedDashboard(bridge);
        if (cached) {
            return cached;
        }
    }

    const link = await serverLink(bridge);
    const name = link ? dashboardBySlug(slug, (await listServerDashboards(bridge, link)).map((entry) => entry.name)) : undefined;
    if (name === undefined) {
        throw new DashboardError(`This Tilez Hub has no dashboard called "${slug}".`);
    }

    return loadFromServer(bridge, name);
}

/** Loads the dashboard again from its source; a source that fails leaves the cached copy as it was. */
export async function reloadDashboard(bridge: Bridge) : Promise<Dashboard> {
    const source = await readSource(bridge);
    if (!source) {
        throw new DashboardError('This device has no dashboard yet: choose or make one in the settings.');
    }

    return loadSource(bridge, source);
}

/** After the dashboard this device shows was renamed in its store: its source follows. */
export async function followRename(bridge: Bridge, kind: 'device' | ServerStore, from: string, to: string) : Promise<void> {
    const source = await readSource(bridge);
    if (source?.kind !== kind || source.name !== from) {
        return;
    }

    await writeSource(bridge, { ...source, name: to });
}

/**
 * Where Save puts the edited dashboard: on this device or the Tilez Hub, under a name. `isNew`:
 * only if no dashboard has that name there ("Save as", a dashboard from a web address).
 */
export type SaveTarget = { kind: 'device'; name: string; isNew: boolean } | { kind: 'server'; store: ServerStore; name: string; condition: WriteCondition };

export type SaveOutcome =
    | { status: 'saved'; dashboard: Dashboard }
    | { status: 'name taken' }
    | { status: 'changed elsewhere'; current: string | undefined };

/**
 * Saves a dashboard changed in edit mode: the stored file with the edits applied (`writeDashboard`;
 * `origins` for tiles the file does not have), so that what the file says otherwise stays as it
 * was. In the store first: on the server, a dashboard changed there since this device loaded it is
 * not overwritten unless the caller asks (S-5), and nothing is written then. Then the device's
 * cached copy, and the source. Throws, and leaves everything as it was, when the result does not
 * validate or the store fails.
 */
export async function saveDashboard(bridge: Bridge, edited: Dashboard, origins: ReadonlyMap<string, TileOrigin>, target: SaveTarget) : Promise<SaveOutcome> {
    const stored = await bridge.storage.read(cachePath());
    if (!stored) {
        throw new DashboardError('There is no stored dashboard to save the changes into.');
    }

    let text: string;
    try {
        text = writeDashboard(new TextDecoder().decode(stored), edited, origins);
    } catch (error) {
        throw new DashboardError(error instanceof Error ? error.message : String(error), [], { cause: error });
    }

    if (target.kind === 'device') {
        if (target.isNew && (await findDeviceDashboard(bridge, target.name)) !== undefined) {
            return { status: 'name taken' };
        }

        parseOrThrow(text, 'as saved');
        await writeDeviceDashboard(bridge, target.name, text);

        return { status: 'saved', dashboard: await adoptDashboard(bridge, text, { kind: 'device', name: target.name }) };
    }

    const written = await writeServerDashboard(bridge, await linkOf(bridge, target.store), target.name, text, target.condition);
    if (!written.ok) {
        return target.condition === 'new' ? { status: 'name taken' } : { status: 'changed elsewhere', current: written.current };
    }

    return { status: 'saved', dashboard: await adoptDashboard(bridge, text, { kind: target.store, name: target.name, version: written.version }) };
}

/**
 * A new, empty dashboard (requirements D-1 to D-3, settings DB-3): the grid the user chose and one
 * screen, "Main", kept at once in `store` under `name`, and shown. Throws when the name is taken.
 */
export async function createDashboard(bridge: Bridge, grid: { columns: number; rows?: number | undefined }, store: 'device' | ServerStore, name: string) : Promise<Dashboard> {
    const file = {
        schemaVersion: SCHEMA_VERSION,
        grid: grid.rows === undefined ? { columns: grid.columns } : { columns: grid.columns, rows: grid.rows },
        connections: {},
        screens: [{ name: 'Main', icon: 'house', tiles: [] }],
    };
    const text = `${JSON.stringify(file, null, 2)}\n`;

    return keepNewDashboard(bridge, text, store, name);
}

/** Keeps `text` in `store` as a new dashboard called `name`, and shows it. Throws when the name is taken. */
export async function keepNewDashboard(bridge: Bridge, text: string, store: 'device' | ServerStore, name: string) : Promise<Dashboard> {
    parseOrThrow(text, `"${name}"`);
    if (store === 'device') {
        if ((await findDeviceDashboard(bridge, name)) !== undefined) {
            throw new DashboardError(`This device has a dashboard called "${name}" already: choose another name.`);
        }

        await writeDeviceDashboard(bridge, name, text);

        return adoptDashboard(bridge, text, { kind: 'device', name });
    }

    const written = await writeServerDashboard(bridge, await linkOf(bridge, store), name, text, 'new');
    if (!written.ok) {
        throw new DashboardError(`${SERVER_NAMES[store].replace(/^the/, 'The')} has a dashboard called "${name}" already: choose another name.`);
    }

    return adoptDashboard(bridge, text, { kind: store, name, version: written.version });
}

// The dashboard app shows what the settings choose without being reloaded (settings UI-2): the
// settings announce a dashboard they loaded, and the dashboard app takes it.
const shown = new EventTarget();

export function announceDashboard(dashboard: Dashboard) : void {
    shown.dispatchEvent(new CustomEvent('dashboard', { detail: dashboard }));
}

export function onDashboardAnnounced(listener: (dashboard: Dashboard) => void) : () => void {
    const handle = (event: Event) : void => listener((event as CustomEvent<Dashboard>).detail);
    shown.addEventListener('dashboard', handle);

    return () => shown.removeEventListener('dashboard', handle);
}

/** Asks the dashboard app to read the dashboard shown again (the settings' Reload, DB-8). */
export function requestReload() : void {
    shown.dispatchEvent(new Event('reload'));
}

export function onReloadRequested(listener: () => void) : () => void {
    shown.addEventListener('reload', listener);

    return () => shown.removeEventListener('reload', listener);
}
